import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { OrganismeFinancement } from '../../types/OrganismeFinancement';
import BackOfficeAPIClient from '../../clients/BackofficeAPIClient';

interface RouteParams {
  id?: string;
}

const apiClient = new BackOfficeAPIClient();

const Form = () => {
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  
  const [organismeFinancement, setOrganismeFinancement] = useState<OrganismeFinancement>({
    Descr: '',
    Adresse: '',
    Actif: true,
  });
  const [updated, setUpdated] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      fetchOrganismeFinancement(parseInt(id));
    }
  }, [id]);

  const fetchOrganismeFinancement = async (IDMatOrgF: number): Promise<void> => {
    try {
      setFetching(true);
      const organismeFinancementData = await apiClient.get<OrganismeFinancement>(`/organismesFinancement/${IDMatOrgF}`);
      setOrganismeFinancement(organismeFinancementData);
    } catch (error) {
      console.log(error);
    } finally {
      setFetching(false);
    }
  };

  const onChangeValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value, type } = event.target;
    const checked = (event.target as HTMLInputElement).checked;

    setOrganismeFinancement(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setUpdated(true);
  };

  const canSubmit = (): boolean => {
    return updated && !fetching && organismeFinancement.Descr !== '';
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (!id) {
      createOrganismeFinancement();
    } else {
      updateOrganismeFinancement();
    }
  };

  const createOrganismeFinancement = async (): Promise<void> => {
    try {
      setSubmitting(true);
      await apiClient.post('/organismesFinancement', organismeFinancement);
      history.push('/organismesFinancement');
    } catch (error) {
      console.log(error);
      alert(error);
      setSubmitting(false);
    }
  };

  const updateOrganismeFinancement = async (): Promise<void> => {
    try {
      setSubmitting(true);
      await apiClient.put(`/organismesFinancement/${organismeFinancement.IDMatOrgF}`, organismeFinancement);
      history.push('/organismesFinancement');
    } catch (error) {
      console.log(error);
      alert(error);
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <h2 className="mb-4">Organismes de financement</h2>

          {fetching ? (
            <div className="d-flex flex-row align-items-center justify-content-center">
              <i className="fa fa-spinner fa-spin fa-fw"></i>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <div className="row">
                <div className="col-lg-6 offset-lg-3">
                  <div className="card">
                    <div className="card-header">
                      {id ? `Modifier ${organismeFinancement.Descr}` : 'Ajouter un organisme de financement'}
                    </div>
                    <div className="card-body pb-3">
                      <div className="form-group row">
                        <label htmlFor="Descr" className="col-sm-3 col-form-label">
                          Description
                        </label>
                        <div className="col-sm-9">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            id="Descr"
                            name="Descr"
                            value={organismeFinancement.Descr}
                            onChange={onChangeValue}
                            maxLength={50}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <label htmlFor="Adresse" className="col-sm-3 col-form-label">
                          Adresse
                        </label>
                        <div className="col-sm-9">
                          <textarea
                            className="form-control form-control-sm"
                            id="Adresse"
                            name="Adresse"
                            value={organismeFinancement.Adresse || ''}
                            onChange={onChangeValue}
                            maxLength={300}
                            rows={4}
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <label htmlFor="Actif" className="col-sm-3 col-form-label">
                          Actif
                        </label>
                        <div className="col-sm-6" style={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            className="form-check-input ml-1"
                            type="checkbox"
                            name="Actif"
                            checked={organismeFinancement.Actif}
                            onChange={onChangeValue}
                          />
                        </div>
                      </div>
                      <div className="row mt-4">
                        <div className="col text-center">
                          <button disabled={!canSubmit()} type="submit" className="btn btn-primary">
                            {submitting && (
                              <span>
                                <i className="fa fa-spinner fa-spin fa-fw"></i>&nbsp;
                              </span>
                            )}
                            Valider
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Form;